import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Package, Shield, Truck, Globe, Headphones, Clock, MapPin, ArrowRight, Code2, KeyRound } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { useI18n } from "../i18n-context";
import { HERO_PHOTOS } from "../assets";

// Fleet carousel — moved from HomePage hero; shows Akwaan branded fleet photos
function FleetCarousel() {
  const { t, lang } = useI18n();
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setActive((i) => (i + 1) % HERO_PHOTOS.length), 4500);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="py-20 md:py-24 bg-brand-paper">
      <div className="container grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <div className="kicker mb-4">
            <span className="kicker-dot" />
            {lang === "ar" ? "أسطولنا يعمل الآن" : "Fleet in action"}
          </div>
          <h2 className="headline text-4xl md:text-5xl text-brand-ink mb-5">
            {lang === "ar"
              ? "شاهد عملياتنا على الأرض"
              : "See our operations in the field"}
          </h2>
          <p className="text-lg text-brand-ink/65 leading-relaxed">
            {lang === "ar"
              ? "صور حقيقية من عمليات أكوان اليومية عبر بغداد والمحافظات — من المستودع إلى تسليم الشحنة."
              : "Real photos from Akwaan's daily operations across Baghdad and the governorates — from depot to doorstep."}
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7 relative"
        >
          <div className="relative rounded-3xl overflow-hidden border-4 border-brand-ink shadow-2xl aspect-[16/10] bg-brand-ink">
            {HERO_PHOTOS.map((src, i) => (
              <motion.img
                key={src}
                src={src}
                alt={`Akwaan fleet ${i + 1}`}
                className="absolute inset-0 w-full h-full object-cover"
                initial={false}
                animate={{ opacity: active === i ? 1 : 0, scale: active === i ? 1.04 : 1 }}
                transition={{ opacity: { duration: 0.9 }, scale: { duration: 5, ease: "linear" } }}
              />
            ))}
            <div className="absolute inset-0 bg-gradient-to-tr from-brand-ink/60 via-transparent to-transparent" />

            <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3 bg-white/95 backdrop-blur-md rounded-2xl p-3">
              <div className="h-10 w-10 rounded-full bg-brand-blue flex items-center justify-center">
                <Truck className="h-5 w-5 text-white" />
              </div>
              <div className={lang === "ar" ? "text-right" : "text-left"}>
                <div className="text-sm font-bold text-brand-ink">{t.marquee[0]}</div>
                <div className="text-xs text-brand-ink/60">{t.marquee[3]}</div>
              </div>
              <div className="ms-auto h-2 w-2 rounded-full bg-green-500 animate-pulse" />
            </div>

            <div className="absolute top-5 right-5 flex items-center gap-2" data-testid="services-carousel-dots">
              {HERO_PHOTOS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  data-testid={`services-dot-${i}`}
                  aria-label={`slide ${i + 1}`}
                  className={`h-2 transition-all rounded-full ${
                    active === i ? "w-8 bg-brand-orange" : "w-2 bg-white/70 hover:bg-white"
                  }`}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  const { t, lang } = useI18n();
  const icons = [Package, Shield, Truck, Globe, Headphones, Clock, Code2, KeyRound];

  return (
    <>
      <PageHeader
        kicker={t.services.kicker}
        title={t.services.title}
        lead={t.services.lead}
        tone="dark"
      />

      <FleetCarousel />

      <section className="section-pad bg-brand-ink text-white relative overflow-hidden noise">
        <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none" />
        <div className="container relative">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {t.services.items.map((s, i) => {
              const Icon = icons[i % icons.length];
              return (
                <div
                  key={i}
                  className="group relative rounded-2xl bg-white/5 border border-white/10 p-7 hover:bg-white/10 hover:border-brand-orange transition-all"
                  data-testid={`service-card-${i}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="h-12 w-12 rounded-xl bg-brand-blue flex items-center justify-center">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <span className="font-mono text-xs text-white/40">0{i + 1}</span>
                  </div>
                  <div className="font-display font-bold text-xl mb-3">{s.t}</div>
                  <div className="text-sm text-white/60 leading-relaxed mb-6">{s.d}</div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-brand-orange opacity-0 group-hover:opacity-100 transition-opacity">
                    {lang === "ar" ? "اعرف المزيد" : "Learn more"}
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COVERAGE */}
      <section className="section-pad">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="kicker justify-center mb-4" data-testid="coverage-kicker">
              <span className="kicker-dot" />
              {t.coverage.kicker}
            </div>
            <h2 className="headline text-4xl md:text-5xl text-brand-ink mb-5">{t.coverage.title}</h2>
            <p className="text-lg text-brand-ink/65 leading-relaxed">{t.coverage.lead}</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {t.coverage.governorates.map((g, i) => (
              <div
                key={i}
                className="group flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-brand-ink/10 hover:border-brand-blue hover:bg-brand-blue hover:text-white transition-all cursor-default"
                data-testid={`coverage-gov-${i}`}
              >
                <MapPin className="h-4 w-4 text-brand-orange group-hover:text-white" />
                <span className="font-medium text-sm">{g}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="container">
          <div className="rounded-3xl bg-brand-blue text-white p-10 md:p-14 text-center">
            <h2 className="headline text-3xl md:text-4xl mb-4">{t.partners.title}</h2>
            <p className="text-white/80 max-w-2xl mx-auto mb-8">{t.partners.lead}</p>
            <Link to="/partners" className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-brand-orange text-white font-bold hover:bg-brand-orangeDark transition-colors">
              {t.partners.form.submit}
              <ArrowRight className="h-4 w-4 flip-x" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
